/**
 * organicShared.jsx — piezas que repiten las tres pestañas de Redes Orgánicas
 * (Instagram, Facebook, TikTok): resumen del periodo con la publicación
 * destacada, la sección de publicaciones (cuadrícula/tabla, orden, vacío), el
 * modal de detalle y las gráficas de forma común (barras por fecha, top y dona
 * por tipo). Lo propio de cada red vive en su *Panel.jsx.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Bar, Doughnut } from 'react-chartjs-2';
import { Link } from 'react-router-dom';
import {
  FaThLarge, FaThList, FaTimes, FaExternalLinkAlt, FaCommentDots, FaSpinner,
  FaSync, FaExclamationTriangle, FaHeart, FaImage, FaStar, FaChevronRight, FaCalendarDay, FaBullhorn,
  FaCalendarAlt,
} from 'react-icons/fa';
import { metaApi } from '../../services/metaService';
import {
  numFmt, compact, shortDate, shortText, longDate, Hint, buildCommentTree, getSocialLocale,
} from '../../components/SocialApi/socialApiShared';

// ── Bloques que se piden aparte del tablero (historias, menciones, meses) ────
// Memoria de la sesión por clave (usuario + periodo) para no repetir al
// cambiar de red.
const lazyCache = new Map();
export function useLazy(key, fetcher) {
  const [state, setState] = useState(() => lazyCache.get(key) || { status: 'loading' });
  useEffect(() => {
    let alive = true;
    const hit = lazyCache.get(key);
    if (hit && hit.status === 'ok' && !hit.data?.refreshing) { setState(hit); return undefined; }
    setState(hit || { status: 'loading' });
    let timer;
    const run = async (retry) => {
      try {
        const data = await fetcher();
        const next = { status: 'ok', data };
        lazyCache.set(key, next);
        if (alive) setState(next);
        // El backend refresca las historias en segundo plano: se vuelve a pedir
        // una sola vez para mostrar las recién guardadas.
        if (data?.refreshing && retry) timer = setTimeout(() => run(false), 15000);
      } catch (e) {
        if (alive) setState({ status: 'error' });
      }
    };
    run(true);
    return () => { alive = false; clearTimeout(timer); };
  }, [key]); // eslint-disable-line react-hooks/exhaustive-deps
  return state;
}

// ── Resumen del periodo: 4 cifras + la publicación destacada ─────────────────
export function PeriodSummary({ t, stats, best, bestTitle, bestMetric, placeholderIcon, onOpen }) {
  return (
    <section className="org-summary">
      <div className="org-summary-stats">
        <h2 className="org-section-title">{t('organic.summaryTitle')}</h2>
        <div className="org-stat-grid">
          {stats.map((s) => (
            <div className="org-stat" key={s.label}>
              <span className="org-stat-value">{s.value}</span>
              <span className="org-stat-label">{s.label}<Hint text={s.hint} /></span>
            </div>
          ))}
        </div>
      </div>
      {best && (
        <button type="button" className="org-best" onClick={() => onOpen(best)}>
          <span className="org-best-thumb">
            {best.thumbnail ? <img src={best.thumbnail} alt="" loading="lazy" /> : placeholderIcon}
            <span className="org-best-badge"><FaStar /> {bestTitle}</span>
          </span>
          <span className="org-best-body">
            <span className="org-best-caption">{shortText(best.caption || best.message, 110) || t('organic.noCaption')}</span>
            <span className="org-best-metric">{bestMetric}</span>
            <span className="org-best-date">{longDate(best.timestamp)}</span>
            <span className="org-best-link">{t('organic.seeDetail')} <FaChevronRight /></span>
          </span>
        </button>
      )}
    </section>
  );
}

// ── Publicaciones: encabezado, orden, vista y estado vacío ───────────────────
// `sorts` = [{ id, label, fn }]; el primero es el orden por defecto.
// En celular siempre va la cuadrícula: la tabla solo deja ver el texto de la
// publicación y las cifras quedan fuera de pantalla.
const NARROW_QUERY = '(max-width: 640px)';
function useNarrow() {
  const get = () => typeof window !== 'undefined' && window.matchMedia?.(NARROW_QUERY).matches;
  const [narrow, setNarrow] = useState(get);
  useEffect(() => {
    const mq = window.matchMedia?.(NARROW_QUERY);
    if (!mq) return undefined;
    const on = () => setNarrow(mq.matches);
    mq.addEventListener?.('change', on);
    return () => mq.removeEventListener?.('change', on);
  }, []);
  return narrow;
}

// Clic en la gráfica por fecha: si ese día hubo una sola publicación se abre su
// detalle; si hubo varias, la sección de publicaciones se filtra a ese día y se
// baja hasta ella. El día es local (una publicación de las 23:00 en México cae
// en su día, no en el siguiente de UTC).
export const dayKey = (ts) => {
  const d = new Date(ts);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('en-CA');
};

export function useDayPick(items, onOpen) {
  const [day, setDay] = useState(null);
  const sectionRef = useRef(null);
  const filtered = useMemo(() => (day ? items.filter((p) => dayKey(p.timestamp) === day) : items), [items, day]);
  // Si el periodo cambia y ese día ya no entra, se suelta el filtro.
  useEffect(() => { if (day && filtered.length === 0) setDay(null); }, [day, filtered.length]);
  const pick = useCallback((post) => {
    const k = dayKey(post.timestamp);
    if (items.filter((p) => dayKey(p.timestamp) === k).length <= 1) { onOpen(post); return; }
    setDay(k);
    requestAnimationFrame(() => sectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }));
  }, [items, onOpen]);
  const clear = useCallback(() => setDay(null), []);
  return { day, items: filtered, pick, clear, sectionRef };
}

export function PostsSection({ t, title, icon, items, sorts, emptyText, range, onRangeChange, renderGrid, renderTable, day, onClearDay, sectionRef }) {
  const [chosenView, setView] = useState('grid');
  const narrow = useNarrow();
  const view = narrow ? 'grid' : chosenView;
  const [sortId, setSortId] = useState(sorts[0].id);
  const sorted = useMemo(() => {
    const s = sorts.find((x) => x.id === sortId) || sorts[0];
    return [...items].sort(s.fn);
  }, [items, sorts, sortId]);

  // Si el periodo se queda sin publicaciones, se ofrece el siguiente más largo
  // con un clic en vez de dejar al usuario adivinando qué filtro mover.
  const longer = range.preset === '7d' ? '30d' : range.preset === '30d' ? '90d' : null;

  return (
    <section className="soc-card org-posts" ref={sectionRef}>
      <div className="soc-posts-head">
        <h2 className="soc-card-title">{icon} {title} <span className="soc-count">{items.length}</span></h2>
        {items.length > 0 && (
          <div className="soc-posts-tools">
            {day && (
              <button type="button" className="soc-chip active" onClick={onClearDay} title={t('organic.clearDayFilter')}>
                <FaCalendarDay /> {longDate(day)} <FaTimes />
              </button>
            )}
            <div className="org-seg small" role="group" aria-label={t('organic.sortRecent')}>
              {sorts.map((s) => (
                <button key={s.id} type="button" className={sortId === s.id ? 'active' : ''} onClick={() => setSortId(s.id)}>
                  {s.label}
                </button>
              ))}
            </div>
            {!narrow && (
              <div className="soc-viewtoggle" role="group">
                <button className={view === 'grid' ? 'active' : ''} onClick={() => setView('grid')} title={t('organic.gridView')} aria-label={t('organic.gridView')}><FaThLarge /></button>
                <button className={view === 'table' ? 'active' : ''} onClick={() => setView('table')} title={t('organic.tableView')} aria-label={t('organic.tableView')}><FaThList /></button>
              </div>
            )}
          </div>
        )}
      </div>
      {items.length === 0 ? (
        <div className="org-empty">
          <p className="org-empty-title">{emptyText}</p>
          {longer && (
            <>
              <p className="org-empty-hint">{t('organic.emptyHint')}</p>
              <button type="button" className="soc-btn primary" onClick={() => onRangeChange({ preset: longer, from: '', to: '' })}>
                {t(longer === '30d' ? 'organic.seeLast30' : 'organic.seeLast90')}
              </button>
            </>
          )}
        </div>
      ) : view === 'grid' ? renderGrid(sorted) : renderTable(sorted)}
    </section>
  );
}

/** Tarjeta de la cuadrícula. `badge` va sobre la miniatura; `metrics` = [{icon, value}]. */
export function PostCard({ post, t, onOpen, badge, metrics, placeholderIcon }) {
  return (
    <div className="soc-post" role="button" tabIndex={0}
      onClick={() => onOpen(post)} onKeyDown={(e) => { if (e.key === 'Enter') onOpen(post); }}>
      <div className="soc-post-thumb">
        {post.thumbnail ? <img src={post.thumbnail} alt="" loading="lazy" /> : <div className="soc-post-ph">{placeholderIcon}</div>}
        {badge && <span className="soc-post-type">{badge}</span>}
      </div>
      <div className="soc-post-body">
        <div className="soc-post-caption">{post.caption || post.message || t('organic.noCaption')}</div>
        <div className="soc-post-meta">
          {metrics.map((m, i) => <span key={i} title={m.title}>{m.icon} {m.value}</span>)}
          <span className="soc-post-date">{shortDate(post.timestamp)}</span>
        </div>
      </div>
    </div>
  );
}

/** Tabla de publicaciones. `columns` = [{ label, title?, render(post) }] después de la publicación y la fecha. */
export function PostsTable({ posts, t, onOpen, columns, firstLabel }) {
  return (
    <div className="soc-table-wrap">
      <table className="soc-table">
        <thead>
          <tr>
            <th>#</th>
            <th>{firstLabel}</th>
            <th>{t('organic.colDate')}</th>
            {columns.map((c) => <th key={c.label} title={c.title}>{c.label}</th>)}
          </tr>
        </thead>
        <tbody>
          {posts.map((p, i) => (
            <tr key={p.id} className="soc-row" onClick={() => onOpen(p)}>
              <td className="soc-rank">{i + 1}</td>
              <td>
                <div className="soc-row-pub">
                  <span className="soc-row-thumb">{p.thumbnail ? <img src={p.thumbnail} alt="" loading="lazy" /> : <FaImage />}</span>
                  <span className="soc-row-cap" title={p.caption || p.message || ''}>{p.caption || p.message || t('organic.noCaption')}</span>
                </div>
              </td>
              <td className="soc-row-date">{longDate(p.timestamp)}</td>
              {columns.map((c) => <td key={c.label} className={c.strong ? 'soc-row-inter' : undefined}>{c.render(p)}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Modal de detalle ─────────────────────────────────────────────────────────
// `children` va junto al texto (p.ej. chips de reacciones); `footer`, a todo lo ancho debajo (comentarios).
export function PostModal({ t, post, onClose, badge, metrics, network, placeholderIcon, children, footer }) {
  const [imgOk, setImgOk] = useState(true);
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="soc-modal-overlay" onClick={onClose}>
      <div className="soc-modal" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
        <button className="soc-modal-close" onClick={onClose} title={t('organic.close')} aria-label={t('organic.close')}><FaTimes /></button>
        <div className="soc-modal-top">
          <div className="soc-modal-preview">
            {post.thumbnail && imgOk
              ? <img src={post.thumbnail} alt="" onError={() => setImgOk(false)} />
              : <div className="soc-modal-ph">{placeholderIcon}</div>}
            {badge && <span className="soc-post-type">{badge}</span>}
          </div>
          <div className="soc-modal-info">
            <div className="soc-modal-meta">
              {metrics.map((m, i) => <span key={i} title={m.title}>{m.icon} {m.value}</span>)}
              <span className="soc-modal-date">{longDate(post.timestamp)}</span>
            </div>
            <p className="soc-modal-caption">{post.caption || post.message || t('organic.noCaption')}</p>
            {children}
            {post.permalink && (
              <a className="soc-modal-link" href={post.permalink} target="_blank" rel="noreferrer">
                {t('organic.viewOn', { network })} <FaExternalLinkAlt />
              </a>
            )}
          </div>
        </div>
        {footer}
      </div>
    </div>
  );
}

/** Comentarios bajo demanda dentro del modal (IG y TikTok). */
export function CommentsBlock({ t, fetchComments }) {
  const [comments, setComments] = useState(null);   // null = aún no cargados
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      setComments(await fetchComments());
    } catch (e) {
      setErr(t('organic.commentsError'));
    } finally {
      setLoading(false);
    }
  }, [fetchComments, t]);

  const tree = useMemo(() => buildCommentTree(comments || []), [comments]);

  return (
    <div className="soc-modal-comments">
      <div className="soc-modal-comments-head">
        <span className="soc-modal-comments-title">
          <FaCommentDots /> {t('organic.commentsTitle')} {comments != null && <b>({comments.length})</b>}
        </span>
        <button className="soc-btn" onClick={load} disabled={loading}>
          {loading ? <><FaSpinner className="soc-spin" /> {t('organic.loadingComments')}</>
            : comments == null ? <><FaCommentDots /> {t('organic.loadComments')}</> : <><FaSync /> {t('organic.reloadComments')}</>}
        </button>
      </div>
      {err && <div className="soc-warn"><FaExclamationTriangle /> {err}</div>}
      {comments != null && comments.length === 0 && !loading && !err && (
        <div className="soc-empty">{t('organic.noComments')}</div>
      )}
      {tree.length > 0 && (
        <ul className="soc-comments">
          {tree.map((c) => <CommentItem key={c.id} c={c} t={t} />)}
        </ul>
      )}
    </div>
  );
}

function CommentItem({ c, t }) {
  return (
    <li className="soc-comment">
      <div className="soc-comment-head">
        <span className="soc-comment-user">@{c.username || t('organic.userFallback')}</span>
        {c.likes > 0 && <span className="soc-comment-likes"><FaHeart /> {numFmt.format(c.likes)}</span>}
        {c.timestamp && <span className="soc-comment-date">{longDate(c.timestamp)}</span>}
      </div>
      <p className="soc-comment-text">{c.text}</p>
      {c.children?.length > 0 && (
        <ul className="soc-comments soc-comments-replies">
          {c.children.map((r) => <CommentItem key={r.id} c={r} t={t} />)}
        </ul>
      )}
    </li>
  );
}

// ── Gráficas comunes ─────────────────────────────────────────────────────────
const legendTop = { position: 'top', align: 'end', labels: { boxWidth: 10, boxHeight: 10, padding: 10, font: { size: 11 } } };

/** Barras apiladas por publicación en orden cronológico. `series` = [{ key, label, color }]. */
export function StackedByDateChart({ items, series, captionOf, onPick }) {
  const byDate = useMemo(() => [...items].sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp)), [items]);
  const data = {
    labels: byDate.map((p) => shortDate(p.timestamp)),
    datasets: series.map((s) => ({
      label: s.label, data: byDate.map((p) => p[s.key] || 0), backgroundColor: s.color,
      borderRadius: 4, stack: 's', maxBarThickness: 34,
    })),
  };
  const options = {
    responsive: true, maintainAspectRatio: false,
    onClick: (_, els) => { if (els.length && onPick) onPick(byDate[els[0].index]); },
    onHover: (e, els) => { if (onPick && e.native?.target) e.native.target.style.cursor = els.length ? 'pointer' : 'default'; },
    plugins: {
      legend: legendTop,
      tooltip: {
        callbacks: {
          title: (it) => { const p = byDate[it[0].dataIndex]; return p ? (shortText(captionOf(p), 44) || shortDate(p.timestamp)) : ''; },
          label: (c) => `${c.dataset.label}: ${numFmt.format(c.parsed.y)}`,
        },
      },
    },
    scales: {
      x: { stacked: true, grid: { display: false }, ticks: { font: { size: 10 }, maxRotation: 0, autoSkip: true } },
      y: { stacked: true, beginAtZero: true, ticks: { callback: (v) => compact(v), font: { size: 10 }, maxTicksLimit: 5 }, grid: { color: '#f1f5f9' } },
    },
  };
  return <Bar data={data} options={options} />;
}

/** Barras agrupadas por día (Facebook: actividad de la Página). */
export function DailyChart({ days, series, hideLegend }) {
  const data = {
    labels: days.map((d) => shortDate(d.date)),
    datasets: series.map((s) => ({
      label: s.label, data: days.map((d) => d[s.key] ?? null), backgroundColor: s.color, borderRadius: 4, maxBarThickness: 26,
    })),
  };
  const options = {
    responsive: true, maintainAspectRatio: false,
    plugins: { legend: hideLegend ? { display: false } : legendTop, tooltip: { callbacks: { label: (c) => `${c.dataset.label}: ${numFmt.format(c.parsed.y)}` } } },
    scales: {
      x: { grid: { display: false }, ticks: { font: { size: 10 }, maxRotation: 0, autoSkip: true } },
      y: { beginAtZero: true, ticks: { callback: (v) => compact(v), font: { size: 10 }, maxTicksLimit: 5 }, grid: { color: '#f1f5f9' } },
    },
  };
  return <Bar data={data} options={options} />;
}

// "2026-09" → "sep 26" en el idioma de la app.
const monthLabel = (ym) => {
  const [y, m] = (ym || '').split('-').map(Number);
  if (!y || !m) return ym;
  return new Date(y, m - 1, 1).toLocaleDateString(getSocialLocale(), { month: 'short', year: '2-digit' });
};
const signed = (n) => `${n > 0 ? '+' : ''}${numFmt.format(n)}`;

/** Seguidores por mes (últimos 12): altas y bajas en barras, neto en el
 *  tooltip. No depende del periodo elegido, así que se pide aparte. */
export function MonthlyFollowersCard({ username, network, color, t }) {
  const state = useLazy(`followers-monthly|${network}|${username}`,
    () => metaApi.organicFollowersMonthly(username, network));
  const months = state.data?.months || [];
  const withData = months.filter((m) => m.net != null);
  const net = withData.reduce((a, m) => a + m.net, 0);
  return (
    <section className="soc-chart-card col-12">
      <div className="soc-chart-head">
        <span className="soc-chart-title"><FaCalendarAlt /> {t('organic.monthlyFollowers')}<Hint text={t('organic.monthlyFollowersHint')} /></span>
        {withData.length > 0 && (
          <span className="soc-chart-tag">{t('organic.monthlyFollowersNet', { n: signed(net), months: withData.length })}</span>
        )}
      </div>
      {state.status === 'loading' ? (
        <div className="org-skel-block chart" />
      ) : withData.length === 0 ? (
        <div className="soc-empty-box">{t(state.status === 'error' || state.data?.error ? 'organic.monthlyFollowersError' : 'organic.monthlyFollowersNone')}</div>
      ) : chartBox(<MonthlyFollowersChart months={months} color={color} t={t} />)}
    </section>
  );
}

function MonthlyFollowersChart({ months, color, t }) {
  // El mes en curso va más claro: todavía no termina.
  const fade = (c, m) => (m.partial ? `${c}80` : c);
  const data = {
    labels: months.map((m) => monthLabel(m.month)),
    datasets: [
      { label: t('organic.followsWord'), data: months.map((m) => m.follows), backgroundColor: months.map((m) => fade(color, m)), borderRadius: 4, maxBarThickness: 30 },
      { label: t('organic.unfollowsWord'), data: months.map((m) => m.unfollows), backgroundColor: months.map((m) => fade('#94a3b8', m)), borderRadius: 4, maxBarThickness: 30 },
    ],
  };
  const options = {
    responsive: true, maintainAspectRatio: false,
    plugins: {
      legend: legendTop,
      tooltip: {
        callbacks: {
          title: (it) => { const m = months[it[0].dataIndex]; return `${monthLabel(m.month)}${m.partial ? ` · ${t('organic.monthInProgress')}` : ''}`; },
          label: (c) => `${c.dataset.label}: ${c.parsed.y == null ? '—' : numFmt.format(c.parsed.y)}`,
          footer: (it) => { const m = months[it[0].dataIndex]; return m.net != null ? `${t('organic.netWord')}: ${signed(m.net)}` : ''; },
        },
      },
    },
    scales: {
      x: { grid: { display: false }, ticks: { font: { size: 10 }, maxRotation: 0, autoSkip: true } },
      y: { beginAtZero: true, ticks: { callback: (v) => compact(v), font: { size: 10 }, maxTicksLimit: 5 }, grid: { color: '#f1f5f9' } },
    },
  };
  return <Bar data={data} options={options} />;
}

/** Top N horizontal. Clic en una barra abre esa publicación. */
export function TopChart({ items, valueOf, captionOf, color, tooltipLabel, onOpen, t }) {
  const top = useMemo(() => [...items].sort((a, b) => valueOf(b) - valueOf(a)).slice(0, 8), [items, valueOf]);
  // En celular el eje de etiquetas se come media tarjeta: se recorta más.
  const labelLen = typeof window !== 'undefined' && window.innerWidth < 640 ? 16 : 28;
  const data = {
    labels: top.map((p) => shortText(captionOf(p), labelLen) || shortDate(p.timestamp) || t('organic.colPost')),
    datasets: [{
      data: top.map(valueOf), backgroundColor: color, hoverBackgroundColor: '#F68D2E',
      borderRadius: 4, barThickness: 'flex', maxBarThickness: 16,
    }],
  };
  const options = {
    indexAxis: 'y', responsive: true, maintainAspectRatio: false,
    onClick: (_, els) => { if (els.length && onOpen) onOpen(top[els[0].index]); },
    onHover: (e, els) => { if (e.native?.target) e.native.target.style.cursor = els.length ? 'pointer' : 'default'; },
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          title: (it) => { const p = top[it[0].dataIndex]; return p ? shortText(captionOf(p), 50) : ''; },
          label: (c) => tooltipLabel(top[c.dataIndex]),
        },
      },
    },
    scales: {
      x: { beginAtZero: true, ticks: { callback: (v) => compact(v), font: { size: 10 } }, grid: { color: '#f1f5f9' } },
      y: { ticks: { font: { size: 10 }, autoSkip: false }, grid: { display: false } },
    },
  };
  return <Bar data={data} options={options} />;
}

/** Dona con total al centro + leyenda. `slices` = [{ label, color, value, sub?, emoji? }]. */
export function DonutCard({ title, tag, hint, slices, centerLabel, emptyText }) {
  const total = slices.reduce((a, s) => a + s.value, 0);
  return (
    <section className="soc-chart-card col-4">
      <div className="soc-chart-head">
        <span className="soc-chart-title">{title}<Hint text={hint} /></span>
        {tag && <span className="soc-chart-tag">{tag}</span>}
      </div>
      {slices.length === 0 || !total ? (
        <div className="soc-empty-box">{emptyText}</div>
      ) : (
        <div className="soc-donut-wrap">
          <div className="soc-donut-chart">
            <Doughnut
              data={{ labels: slices.map((s) => s.label), datasets: [{ data: slices.map((s) => s.value), backgroundColor: slices.map((s) => s.color), borderColor: '#fff', borderWidth: 2, hoverOffset: 5 }] }}
              options={{
                responsive: true, maintainAspectRatio: false, cutout: '66%',
                plugins: { legend: { display: false }, tooltip: { callbacks: { label: (c) => `${c.label}: ${numFmt.format(c.parsed)} (${Math.round((c.parsed / total) * 100)}%)` } } },
              }}
            />
            <div className="soc-donut-center"><span className="v">{compact(total)}</span><span className="t">{centerLabel}</span></div>
          </div>
          <div className="soc-legend">
            {slices.map((s) => (
              <div className="soc-leg" key={s.label}>
                {s.emoji ? <span className="fb-rx-emoji" aria-hidden="true">{s.emoji}</span> : <span className="soc-leg-dot" style={{ background: s.color }} />}
                <span className="soc-leg-name">{s.label}</span>
                <span className="soc-leg-val">{compact(s.value)}<span className="cnt">{s.sub ?? `${Math.round((s.value / total) * 100)}%`}</span></span>
              </div>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}

/** Agrupa publicaciones por tipo: [{ label, color, value (interacciones), count }], mayor a menor. */
export function groupByType(posts, typeOf, valueOf) {
  const map = new Map();
  for (const p of posts) {
    const info = typeOf(p);
    const cur = map.get(info.label) || { label: info.label, color: info.color, value: 0, count: 0 };
    cur.value += valueOf(p);
    cur.count += 1;
    map.set(info.label, cur);
  }
  return [...map.values()].sort((a, b) => b.value - a.value);
}

/** Engagement por seguidores: interacciones promedio por publicación del
 *  periodo ÷ seguidores. Es la misma fórmula en las tres redes para que se
 *  puedan comparar entre sí. null si no hay publicaciones o seguidores. */
export function engagementRate(items, followers) {
  if (!items.length || !followers) return null;
  return items.reduce((a, p) => a + (p.inter || 0), 0) / items.length / followers;
}

export const chartBox = (children, tall) => <div className={`soc-chart-box ${tall ? 'tall' : ''}`}>{children}</div>;

/** Título de sección con subtítulo y un extra a la derecha (p.ej. "Actualizando…"). */
export function SectionHead({ title, sub, extra }) {
  return (
    <div className="org-section-head">
      <h2 className="org-section-title">{title}</h2>
      {sub && <span className="org-section-sub">{sub}</span>}
      {extra && <span className="org-section-extra">{extra}</span>}
    </div>
  );
}

/** Barras horizontales simples en CSS (edad, ciudades): más legibles en celular
 *  que un gráfico con ejes. `rows` = [{ label, pct }]. */
export function BarList({ rows, color }) {
  const max = Math.max(1, ...rows.map((r) => r.pct));
  return (
    <ul className="org-barlist">
      {rows.map((r) => (
        <li key={r.label}>
          <span className="org-barlist-label" title={r.label}>{r.label}</span>
          <span className="org-bar"><span style={{ width: `${(r.pct / max) * 100}%`, background: color }} /></span>
          <span className="org-barlist-val">{r.pct}%</span>
        </li>
      ))}
    </ul>
  );
}

/** Rejilla de cifras con etiqueta (resumen de historias, detalle de publicación). */
export function MetricGrid({ items, compact: small }) {
  return (
    <div className={`org-metric-grid ${small ? 'compact' : ''}`}>
      {items.map((m) => (
        <div className="org-metric" key={m.label}>
          <span className="org-metric-value">{m.icon}{m.value}</span>
          <span className="org-metric-label">{m.label}<Hint text={m.hint} /></span>
        </div>
      ))}
    </div>
  );
}

/** Aviso de una línea: cuánto sumó la pauta, con salida a Campañas. */
export function PaidNote({ t, n }) {
  return (
    <div className="org-paid-note">
      <FaBullhorn />
      <span>{t('organic.paidNote', { n })}</span>
      <Link to="/campaigns-api" className="org-paid-link">{t('organic.seeAds')} <FaChevronRight /></Link>
    </div>
  );
}
